import { useState, useEffect } from 'react';
import { FiAlertCircle, FiCheck, FiBriefcase } from 'react-icons/fi';
import API_URL from '../config';

export default function AcceptInvite({ inviteToken, onAccepted }) {
  const [invite, setInvite] = useState(null);
  const [step, setStep] = useState('loading'); // loading | form | done | error
  const [errorMsg, setErrorMsg] = useState('');

  // Owner/Manager fields
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [managerPin, setManagerPin] = useState('');
  const [managerPinConfirm, setManagerPinConfirm] = useState('');

  // Employee fields
  const [empName, setEmpName] = useState('');
  const [passcode, setPasscode] = useState('');

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!inviteToken) { setStep('error'); setErrorMsg('No invite token in URL.'); return; }
    fetch(`${API_URL}/api/auth/invite/${inviteToken}`)
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (ok) { setInvite(d); setStep('form'); }
        else { setStep('error'); setErrorMsg(d.error || 'Invite not found or expired.'); }
      })
      .catch(() => { setStep('error'); setErrorMsg('Connection error.'); });
  }, [inviteToken]);

  const submitOwner = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setErrorMsg('Passwords do not match.'); return; }
    if (pin && !/^\d{6}$/.test(pin)) { setErrorMsg('Kiosk PIN must be exactly 6 digits.'); return; }
    if (pin && pin !== pinConfirm) { setErrorMsg('Kiosk PINs do not match.'); return; }
    if (!managerPin) { setErrorMsg('Discount Approval PIN is required.'); return; }
    if (!/^\d{6}$/.test(managerPin)) { setErrorMsg('Discount Approval PIN must be exactly 6 digits.'); return; }
    if (managerPin !== managerPinConfirm) { setErrorMsg('Discount Approval PINs do not match.'); return; }
    if (pin && managerPin === pin) { setErrorMsg('Discount Approval PIN must be different from your kiosk PIN.'); return; }
    setErrorMsg(''); setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/accept-owner/${inviteToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, password, manager_pin: managerPin, ...(pin ? { pin } : {}) }),
      });
      const data = await res.json();
      if (res.ok) {
        if (onAccepted) {
          window.history.replaceState({}, '', '/business/');
          onAccepted(data.token, data);
        } else {
          setStep('done');
        }
      } else {
        setErrorMsg(data.error || 'Signup failed.');
      }
    } catch { setErrorMsg('Connection error.'); }
    finally { setSubmitting(false); }
  };

  const submitEmployee = async (e) => {
    e.preventDefault();
    if (!passcode.match(/^\d{4}$/)) { setErrorMsg('Employee passcode must be exactly 4 digits.'); return; }
    setErrorMsg(''); setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/accept-employee/${inviteToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: empName, passcode }),
      });
      const data = await res.json();
      if (res.ok) setStep('done');
      else setErrorMsg(data.error || 'Signup failed.');
    } catch { setErrorMsg('Connection error.'); }
    finally { setSubmitting(false); }
  };

  if (step === 'loading') return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <p className="text-slate-500 text-sm">Loading invite…</p>
    </div>
  );

  if (step === 'error') return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="bg-white rounded border border-red-200 p-8 max-w-sm w-full text-center shadow-sm">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
          <FiAlertCircle size={22} className="text-red-600" />
        </div>
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Invite Invalid</h2>
        <p className="text-slate-500 text-sm">{errorMsg}</p>
      </div>
    </div>
  );

  if (step === 'done') return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="bg-white rounded border border-green-200 p-8 max-w-sm w-full text-center shadow-sm">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <FiCheck size={22} className="text-green-600" />
        </div>
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Account Created!</h2>
        {invite?.role === 'EMPLOYEE' ? (
          <p className="text-slate-500 text-sm">Your account is ready. Go to the kiosk and enter your passcode to clock in.</p>
        ) : (
          <>
            <p className="text-slate-500 text-sm mb-5">
              Your account for <strong>{invite?.business_name}</strong> is ready.
              {pin ? ' Your kiosk PIN is set — you can clock in on the employee kiosk too.' : ' You can set a kiosk PIN any time from the My PIN tab in the portal.'}
              {' '}Your discount approval PIN is set.
            </p>
            <a
              href={window.location.origin}
              className="inline-block w-full py-2.5 bg-blue-700 text-white text-sm font-semibold rounded hover:bg-blue-800 transition-colors"
            >
              Go to Manager Portal →
            </a>
          </>
        )}
      </div>
    </div>
  );

  // ── Form ──
  const isEmployee = invite?.role === 'EMPLOYEE';

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-[400px]">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded bg-blue-700 mb-4 shadow-sm">
            <FiBriefcase size={26} className="text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">{invite?.business_name}</h1>
          <p className="text-slate-500 text-sm mt-1">
            You've been invited as <strong>{invite?.role}</strong>
          </p>
        </div>

        <div className="bg-white rounded border border-slate-200 p-5 sm:p-8 shadow-sm">
          <h2 className="text-base font-semibold text-slate-800 mb-5">Set up your account</h2>

          {isEmployee ? (
            <form onSubmit={submitEmployee} className="space-y-4">
              <div>
                <label className="inv-label">Your Name</label>
                <input
                  type="text" value={empName} onChange={e => setEmpName(e.target.value)}
                  required className="inv-input"
                  placeholder="e.g. Hein Khant Zaw"
                />
              </div>
              <div>
                <label className="inv-label">Kiosk Passcode (4 digits)</label>
                <input
                  type="password" inputMode="numeric" value={passcode} onChange={e => setPasscode(e.target.value)}
                  required maxLength={4} className="inv-input font-mono tracking-widest"
                  placeholder="••••"
                />
                <p className="text-slate-400 text-xs mt-1">You'll enter this 4-digit code on the kiosk to clock in/out.</p>
              </div>
              {errorMsg && <p className="text-red-600 text-sm">{errorMsg}</p>}
              <button type="submit" disabled={submitting} className="inv-btn-primary">
                {submitting ? 'Creating account…' : 'Create Account'}
              </button>
            </form>
          ) : (
            <form onSubmit={submitOwner} className="space-y-4">
              <div>
                <label className="inv-label">Your Name</label>
                <input
                  type="text" value={name} onChange={e => setName(e.target.value)}
                  required className="inv-input"
                  placeholder="e.g. Mg Mg"
                />
              </div>
              <div>
                <label className="inv-label">Password</label>
                <input
                  type="password" value={password} onChange={e => setPassword(e.target.value)}
                  required minLength={8} className="inv-input"
                  placeholder="Min 8 characters"
                />
              </div>
              <div>
                <label className="inv-label">Confirm Password</label>
                <input
                  type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                  required className="inv-input"
                  placeholder="Repeat password"
                />
              </div>

              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Kiosk PIN (optional)</p>
                <div className="space-y-3">
                  <div>
                    <label className="inv-label">PIN (6 digits)</label>
                    <input
                      type="password" inputMode="numeric" value={pin}
                      onChange={e => setPin(e.target.value)} maxLength={6}
                      className="inv-input font-mono tracking-widest"
                      placeholder="••••••"
                    />
                    <p className="text-slate-400 text-xs mt-1">Set this 6-digit PIN to clock in/out on the kiosk. You can also set it later in the portal.</p>
                  </div>
                  {pin && (
                    <div>
                      <label className="inv-label">Confirm PIN</label>
                      <input
                        type="password" inputMode="numeric" value={pinConfirm}
                        onChange={e => setPinConfirm(e.target.value)} maxLength={6}
                        className="inv-input font-mono tracking-widest"
                        placeholder="••••••"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Discount Approval PIN</p>
                <p className="text-slate-400 text-xs mb-3">You'll enter this at the POS to approve discounts. Must be 6 digits and different from your kiosk PIN.</p>
                <div className="space-y-3">
                  <div>
                    <label className="inv-label">Discount Approval PIN (6 digits) *</label>
                    <input
                      type="password" inputMode="numeric" value={managerPin}
                      onChange={e => setManagerPin(e.target.value)} maxLength={6}
                      required className="inv-input font-mono tracking-widest"
                      placeholder="••••••"
                    />
                  </div>
                  <div>
                    <label className="inv-label">Confirm Discount PIN</label>
                    <input
                      type="password" inputMode="numeric" value={managerPinConfirm}
                      onChange={e => setManagerPinConfirm(e.target.value)} maxLength={6}
                      required className="inv-input font-mono tracking-widest"
                      placeholder="••••••"
                    />
                  </div>
                </div>
              </div>

              {errorMsg && <p className="text-red-600 text-sm">{errorMsg}</p>}
              <button type="submit" disabled={submitting} className="inv-btn-primary">
                {submitting ? 'Creating account…' : 'Create Account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
